import { Button, GuestInputState } from "@/client/input";
import { SimpleEventDispatcher } from "strongly-typed-events";
import { InputPlugin } from "@/client/plugins";
import { match } from "ts-pattern";
import { MouseInputSchema } from "@/client/communication/api/bindings/MouseInputSchema";
import { Camera } from "@/client/camera";

import {
  MAX_MOUSE_DISTANCE,
  MIN_MOUSE_DISTANCE,
  RECONNECT_DISTANCE,
} from "@/client/renderer/setup_mouse_effects";

const plugin_id = "MOUSE";
let activate_plugin$: SimpleEventDispatcher<string>;
const mouse_position = { x: 0, y: 0 };
let mouse_active = false;
let stage_width = window.innerWidth;
let stage_height = window.outerHeight;
let MIN_DISTANCE = 50;
let MAX_DISTANCE = 100;
let REC_DISTANCE = 100;
let x_axis = 0;
let y_axis = 0;
const set_stage_size = () => {
  const canvas_wrapper = document.getElementById("canvas");
  if (!canvas_wrapper) {
    return;
  }
  stage_width = canvas_wrapper.offsetWidth;
  stage_height = canvas_wrapper.offsetHeight;
  const stage_size = Math.max(stage_width, stage_height);
  MIN_DISTANCE = MIN_MOUSE_DISTANCE();
  MAX_DISTANCE = MAX_MOUSE_DISTANCE(stage_size);
  REC_DISTANCE = RECONNECT_DISTANCE(stage_size);
};

window.addEventListener("resize", set_stage_size);

export type MousePluginType = InputPlugin<{
  mouse_mode: MouseInputSchema;
  mouse_state: {
    mouse_position: typeof mouse_position;
    connected: boolean;
    is_active: boolean;
  };
}>;

const mouse_state = {
  mouse_position,
  connected: false,
  is_active: false,
};

const plugin: MousePluginType = {
  initialize: mouse_input,
  id: plugin_id,
  plugin_options: {
    mouse_mode: "PurelyDirectionalNoJump",
    mouse_state,
  },
  update: (is_active) => (mouse_state.is_active = is_active),
};

function mouse_input(
  guest_input: GuestInputState,
  activate_plugin: SimpleEventDispatcher<string>,
  camera_update: SimpleEventDispatcher<Camera>,
) {
  set_stage_size();
  setTimeout(set_stage_size, 100);
  const toggle_chat_element = document.getElementById("toggle-chat");
  if (toggle_chat_element) {
    toggle_chat_element.addEventListener("click", () => {
      setTimeout(set_stage_size, 100);
    });
  }
  activate_plugin$ = activate_plugin;
  let camera: Camera | null = null;
  activate_plugin$.subscribe((input_plugin_id) => {
    if (mouse_active && input_plugin_id !== plugin_id) {
      set_button(guest_input, Button.Left, false);
      set_button(guest_input, Button.Right, false);
      set_button(guest_input, Button.Up, false);
      set_button(guest_input, Button.Down, false);
      set_button(guest_input, Button.Jump, false);
    }

    mouse_active = input_plugin_id === plugin_id;

    if (mouse_active) {
      guest_input.x_axis_callback = () => x_axis;
      guest_input.y_axis_callback = () => y_axis;
    }
  });

  camera_update.subscribe((camera_instance) => {
    camera = camera_instance;
    if (!mouse_state.is_active) {
      return;
    }
    if (mouse_active && camera) {
      update_mouse_input(mouse_position, guest_input, camera);
    }
    set_mouse_button_cursor(guest_input);
  });

  document.addEventListener("mousemove", (mouse_event: MouseEvent) => {
    mouse_position.x = mouse_event.x;
    mouse_position.y = mouse_event.y;
  });

  document.addEventListener(
    "visibilitychange",
    function () {
      if (document.hidden) {
        reset_all_buttons(guest_input);
      }
    },
    false,
  );

  document.addEventListener("mouseleave", function (event) {
    if (
      event.clientY <= 0 ||
      event.clientX <= 0 ||
      event.clientX >= window.innerWidth ||
      event.clientY >= window.innerHeight
    ) {
      reset_all_buttons(guest_input);
    }
  });
}

function set_mouse_button_cursor(guest_input: GuestInputState) {
  const canvas = document.querySelector("#canvas canvas");
  if (!canvas) {
    console.error("Button feedback elements are missing!");
    return;
  }
  const classes = [];
  for (const button in Button) {
    if (guest_input.button_pressed_map[button]) {
      classes.push(`button-${button.toLowerCase()}`);
    }
  }
  const className = classes.join(" ");
  if (canvas.className !== className) {
    canvas.className = className;
  }
}

function reset_all_buttons(guest_input: GuestInputState) {
  set_button(guest_input, Button.Left, false);
  set_button(guest_input, Button.Right, false);
  set_button(guest_input, Button.Up, false);
  set_button(guest_input, Button.Down, false);
  set_button(guest_input, Button.Jump, false);
}

function get_offset_mouse_from_camera(camera: Camera) {
  const entity_position = camera.get_last_entity_ref_position();
  const camera_iso = camera.camera_isometry;
  const offset_mouse_x = (camera_iso.x - entity_position.x) / camera.zoom;
  const offset_mouse_y = (camera_iso.y - entity_position.y) / camera.zoom;
  return { offset_mouse_x, offset_mouse_y };
}

function update_mouse_input(
  mouse_event: typeof mouse_position,
  guest_input: GuestInputState,
  camera: Camera,
) {
  const stage_width_half = stage_width / 2;
  const stage_height_half = stage_height / 2;

  const { offset_mouse_x, offset_mouse_y } =
    get_offset_mouse_from_camera(camera);

  const cursor_x = mouse_event.x - stage_width_half + offset_mouse_x;
  const cursor_y = mouse_event.y - stage_height_half + offset_mouse_y;
  const distance = Math.sqrt(cursor_x ** 2 + cursor_y ** 2);
  const angle_in_degrees = (Math.atan2(cursor_y, cursor_x) * 180) / Math.PI;

  if (!mouse_state.connected && distance < REC_DISTANCE) {
    console.log("Reconnected mouse");

    mouse_state.connected = true;
  }
  if (mouse_state.connected && distance > MAX_DISTANCE) {
    console.log("Disconnected mouse");
    mouse_state.connected = false;
    reset_all_buttons(guest_input);
    set_axis(guest_input, 0, 0);
  }
  if (!mouse_state.connected) {
    return;
  }

  if (distance < MIN_DISTANCE || distance > MAX_DISTANCE) {
    set_axis(guest_input, 0, 0);
  } else {
    const direction_x = cursor_x / distance;
    const direction_y = cursor_y / distance;
    const pct = (distance - MIN_DISTANCE) / (MIN_DISTANCE * 2);
    set_axis(guest_input, direction_x * pct, direction_y * pct);
  }

  match(plugin.plugin_options.mouse_mode)
    .with("UpIsJumpAndNoDown", () => {
      set_button(guest_input, Button.Jump, cursor_y < -50);
      left_right_standard(angle_in_degrees, distance, guest_input);
    })
    .with("PurelyDirectionalNoJump", () => {
      up_down_standard(angle_in_degrees, distance, guest_input);
      left_right_standard(angle_in_degrees, distance, guest_input);
    })
    .exhaustive();
}

function set_axis(guest_input: GuestInputState, x: number, y: number) {
  if (guest_input.x_axis_callback() !== x) {
    x_axis = x;
    guest_input.is_dirty = true;
  }
  if (guest_input.y_axis_callback() !== y) {
    y_axis = y;
    guest_input.is_dirty = true;
  }
}

function left_right_standard(
  angle_in_degrees: number,
  distance: number,
  guest_input: GuestInputState,
) {
  if (distance < MIN_DISTANCE || distance > MAX_DISTANCE) {
    set_button(guest_input, Button.Left, false);
    set_button(guest_input, Button.Right, false);
    return;
  }
  const angle_abs = Math.abs(angle_in_degrees);
  if (angle_abs <= 180 && angle_abs > 120) {
    set_button(guest_input, Button.Left, true);
    set_button(guest_input, Button.Right, false);
  } else if (angle_abs <= 60 && angle_abs > 0) {
    set_button(guest_input, Button.Right, true);
    set_button(guest_input, Button.Left, false);
  } else {
    set_button(guest_input, Button.Left, false);
    set_button(guest_input, Button.Right, false);
  }
}

function up_down_standard(
  angle_in_degrees: number,
  distance: number,
  guest_input: GuestInputState,
) {
  if (distance < MIN_DISTANCE || distance > MAX_DISTANCE) {
    set_button(guest_input, Button.Up, false);
    set_button(guest_input, Button.Down, false);
    return;
  }

  if (angle_in_degrees < -30 && angle_in_degrees > -150) {
    set_button(guest_input, Button.Up, true);
    set_button(guest_input, Button.Down, false);
  } else if (angle_in_degrees > 30 && angle_in_degrees < 150) {
    set_button(guest_input, Button.Up, false);
    set_button(guest_input, Button.Down, true);
  } else {
    set_button(guest_input, Button.Up, false);
    set_button(guest_input, Button.Down, false);
  }
}

function set_button(
  guest_input: GuestInputState,
  button: Button,
  button_state: boolean,
) {
  if (guest_input.button_pressed_map[button] !== button_state) {
    guest_input.button_pressed_map[button] = button_state;

    guest_input.is_dirty = true;
  }
}

if (window.register_input_plugin) {
  window.register_input_plugin(plugin);
}
